import React from 'react';
import { useRouter } from 'next/router';

import dayjs from 'dayjs';
import { useCurrentUser } from '@docavenue/components';
import { useSelector } from 'react-redux';
import { AmcProtectedPatientDTO } from '@maiia/model/generated/model/api-patient/api-patient';
import { Typography, cn } from '@kiiwi/ui';
import { Trans, useTranslation } from '@/src/i18n';
import RejectionDialog, {
  RejectionDialogStatus,
} from '@/components/templates/TlcAmc/Steps/SelectPatient/RejectionDialog';
import { getPrimaryProtectedPatient } from '@/components/templates/TlcAmc/utils';
import ListHeader from '../SearchResults/ListHeader';
import FontAwesomeIcon from '@/components/atoms/FontawesomeIcon/FontawesomeIcon';
import { getPicture } from '@/components/utils/card';
import { checkVideoStatusIs } from '@/components/templates/TlcSession/utils';
import { analyticsEvent } from '@/src/analytic_tag_manager';
import { APP_ROUTES } from '@/src/constants';

const rejectionDialogInitialState = {
  isOpen: false,
  rejectionReason: null,
};

type Props = {
  protectedPatientItems: AmcProtectedPatientDTO[];
  isEncouraged?: boolean;
};

const TlcAmcCard = ({ protectedPatientItems, isEncouraged }: Props) => {
  const router = useRouter();
  const { t } = useTranslation();
  const currentUser = useCurrentUser();
  const defaultPatient = currentUser?.userPatientInformation?.userPatientProfiles?.find(
    patient => patient.isDefault,
  );
  const activeSession = useSelector((state: any) => state?.videoSessions?.item);

  const [rejectionDialog, setRejectionDialog] = React.useState<{
    isOpen: boolean;
    rejectionReason: RejectionDialogStatus;
  }>(rejectionDialogInitialState);

  const clearRejection = () => setRejectionDialog(rejectionDialogInitialState);

  const protectedPatient = React.useMemo(
    () => getPrimaryProtectedPatient(protectedPatientItems, defaultPatient),
    [protectedPatientItems, currentUser],
  );

  const expiredProtectedPatient =
    protectedPatient?.linkType !== 'IDB' &&
    dayjs().isAfter(dayjs(protectedPatient?.expirationDate));

  const startTlcAmcFlow = (action: 'ClicCardTLCAMC' | 'ClicCardTLCIconAMC') => {
    if (
      checkVideoStatusIs(activeSession?.videoSessionStatus, [
        'PENDING',
        'WAITING',
        'STARTED',
      ])
    ) {
      router.push({
        pathname: '/tlc/session/[id]',
        query: {
          id: activeSession.id,
        },
      });
    } else if (expiredProtectedPatient) {
      setRejectionDialog({
        isOpen: true,
        rejectionReason: 'linkExpired',
      });
    } else {
      analyticsEvent({
        category: 'TLCAMC',
        action,
      });

      router.push({
        pathname: APP_ROUTES.TLC.AMC,
      });
    }
  };

  const handleClickCard = () => {
    startTlcAmcFlow('ClicCardTLCAMC');
  };

  const handleClickCardButton = (e: React.MouseEvent<HTMLDivElement>) => {
    startTlcAmcFlow('ClicCardTLCIconAMC');
    e.stopPropagation(); // Prevent the click event from bubbling up to the parent
  };

  return (
    <>
      {!isEncouraged && (
        <ListHeader
          titleKey="tlc-amc-card-title"
          subtitleKey="tlc-amc-card-subtitle"
        />
      )}
      <div
        className={cn(
          'relative flex w-full max-w-[760px] cursor-pointer gap-2 rounded-lg border border-neutral bg-surface-white p-3 sm:gap-3.5',
          !isEncouraged && 'mt-6',
        )}
        onClick={handleClickCard}
      >
        <div className="hidden w-full max-w-[158px] items-center justify-center sm:flex">
          <img
            src={getPicture(protectedPatient?.amcPicture?.thumbnailS3Id)}
            alt={`${protectedPatient?.amcName} logo`}
          />
        </div>
        <div className="flex flex-1 flex-col overflow-hidden">
          <div className="mb-2 max-h-[36px] sm:hidden">
            <img
              src={getPicture(protectedPatient?.amcPicture?.thumbnailS3Id)}
              alt={`${protectedPatient?.amcName} logo`}
              className="h-full"
            />
          </div>
          <Typography className="text-md font-semibold text-neutral-primary sm:text-lg">
            <Trans
              i18nKey={
                protectedPatient?.service?.offer === 'TLC_8_TO_20'
                  ? 'tlc-amc-card-8-20-title'
                  : 'tlc-amc-card-24-24-title'
              }
              components={{
                breaklinedesktop: <span className="hidden sm:block" />,
                spacemobile: <span className="inline sm:hidden" />,
              }}
            />
          </Typography>
          <Typography className="mt-1 text-sm text-neutral-secondary">
            <Trans
              i18nKey={
                protectedPatient?.service?.offer === 'TLC_8_TO_20'
                  ? 'tlc-amc-card-8-20-subtitle'
                  : 'tlc-amc-card-24-24-subtitle'
              }
              components={{
                breaklinedesktop: <span className="hidden sm:block" />,
                spacemobile: <span className="inline sm:hidden" />,
              }}
            />
          </Typography>
        </div>

        <div
          className="flex w-full max-w-[121px] flex-1 cursor-pointer flex-col items-center justify-center rounded-sm bg-brand-secondary-hovered p-3 sm:max-w-[216px]"
          onClick={handleClickCardButton}
        >
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-primary-enabled">
            <FontAwesomeIcon
              name="video:solid"
              className="h-4 w-4 text-white"
            />
          </div>
          <Typography className="mt-1 text-base font-semibold text-brand-secondary-enabled sm:text-lg">
            {t('consult')}
          </Typography>
        </div>
      </div>

      <RejectionDialog
        rejectionReason={rejectionDialog.rejectionReason}
        isOpen={rejectionDialog.isOpen}
        clearRejection={clearRejection}
      />
    </>
  );
};

export default TlcAmcCard;
